<!-- Mobile sign-in component that handles phone-based authentication
Features mobile number validation, OTP generation, and error handling
Implements a two-step authentication process with mobile and OTP verification -->

<template>
  <!-- Authentication header section -->
  <div class="auth-header">
    <h1 class="auth-title">
      {{ LOGIN_CONSTANTS.SIGN_IN_TITLE }}
    </h1>
    <p v-if="!isOtpSent" class="auth-subtitle">
      {{ LOGIN_CONSTANTS.SIGN_IN_MOBILE_SUBTITLE }}
    </p>
    <p v-else class="auth-subtitle">
      {{ LOGIN_CONSTANTS.SIGN_IN_OTP_SUBTITLE_MOBILE }}
    </p>
  </div>

  <!-- Mobile number input form -->
  <v-form v-if="!isOtpSent" class="auth-form" @submit.prevent="onSubmit">
    <!-- Mobile number input field -->
    <div class="form-group">
      <InputWithValidation
        id="mobile"
        name="mobile_number"
        label="Mobile No."
        data-testid="mobile-number-input"
        :placeholder="LOGIN_CONSTANTS.MOBILE_PLACEHOLDER"
        aria-label="Enter your Mobile No."
        :rules="[(value: string) => !!value || 'Mobile No. is required']"
        required
      />
    </div>

    <!-- Submit button -->
    <v-btn
      type="submit"
      class="submit-button"
      elevation="2"
      block
      :loading="isLoading"
      data-testid="mobile-submit-button"
    >
      {{ COMMON_CONSTANTS.BUTTON.SUBMIT }}
    </v-btn>
  </v-form>

  <!-- OTP verification form -->
  <div v-else>
    <OtpForm :phone="formData.mobile_number" />
    <v-btn
      variant="text"
      prepend-icon="mdi-arrow-left"
      class="mb-4 text-grey-darken-1"
      @click="goBack"
    >
      {{ LOGIN_CONSTANTS.BACK_TO_SIGN_IN }}
    </v-btn>
  </div>
</template>

<script setup lang="ts">
// Form validation and utility imports
import { useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import { LOGIN_CONSTANTS } from "~/constants/login-constants";
import { COMMON_CONSTANTS } from "~/constants/common-constants";
import {
  phoneSchema,
  type PhoneModel,
} from "~/utils/validation/emailPhoneSchema";
import type {
  IEmailMobileLoginResponse,
  LoginRequestBody,
} from "~/types/login";
import { API_ENDPOINT } from "~/constants/api-endpoint";

// Composables initialization
const { isLoading } = useCommon();
const { handleErrorResponse } = useErrors();
const { showSnackbar } = useSnackbar();
const loginStore = useLoginStore();

// Form data initialization
const formData = ref<PhoneModel>({
  mobile_number: "",
});

// State management
const errorMessage = ref("");
const isOtpSent = computed(() => loginStore.getIsOtpSent);
const loginType = useState<string>("loginType");
const organizationId = useState<string>("organizationId");

// Form validation setup
const { handleSubmit, setFieldError } = useForm({
  validationSchema: toTypedSchema(phoneSchema),
});

/**
 * Handles form submission
 * Validates mobile number and triggers OTP generation
 * Manages error states and success notifications
 */
const onSubmit = handleSubmit(async (values: PhoneModel) => {
  isLoading.value = true;

  // Prepare login request body
  const body: LoginRequestBody = {
    ...values,
    login_type: loginType.value,
    organization_id: organizationId.value,
    is_public_access: loginStore.loginTypeData.is_public_access,
  };

  // Update form data with submitted mobile number
  formData.value.mobile_number = values.mobile_number;

  try {
    // Send login request and handle response
    const response = await useApi().post<IEmailMobileLoginResponse>(
      API_ENDPOINT.LOGIN,
      body
    );

    loginStore.setMobileNumber(values.mobile_number);
    // Update OTP sent state and show success message
    loginStore.setIsOtpSent(true);
    showSnackbar(response.message, "success");
    return response;
  } catch (error: any) {
    // Handle error response and show error message
    errorMessage.value = handleErrorResponse(error.response).error;
    showSnackbar(errorMessage.value, "error");

    // Handle field errors from server
    if (error.response.data.errors) {
      Object.entries(error.response.data.errors).forEach(([field, message]) => {
        setFieldError(field as keyof PhoneModel, message as string);
      });
    }
  } finally {
    // Reset loading state
    isLoading.value = false;
  }
});

const goBack = () => {
  loginStore.clearIsOtpSent();
};
</script>
